/**
 * FilterSelect — a compact select that is not part of a form: a list's scoped filter in the `Toolbar` right slot
 * (entity-management §1.3), or a one-off picker ("Add model"). Demo page: /tables/data-tables (header selects);
 * NextAdmin `last-transactions-table`. In a form use `SelectFormField`.
 *
 * `''` is "nothing chosen": with `anyLabel` it renders that option ("All departments"), without it the placeholder.
 * The select stays controlled (`null`, never `undefined`), so clearing the value clears what it shows.
 */
import {
  Select,
  SelectContent,
  SelectIndicator,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../tailgrids/core/select.js';
import { cn } from '../../utils/cn.js';

const ANY = '__any__';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSelectProps {
  /** Names the select for screen readers ("Department"); it is not drawn. */
  label: string;
  /** The chosen value, or `''` for none. */
  value: string;
  options: readonly FilterOption[];
  onChange: (value: string) => void;
  /** A filter's "any" option ("All departments"), chosen when the value is `''`. Leave it out for a picker. */
  anyLabel?: string;
  /** What a picker shows while nothing is chosen. */
  placeholder?: string;
  /** Shown but not changeable (a view-only person, a save in flight). */
  isDisabled?: boolean;
  className?: string;
}

export function FilterSelect({
  label,
  value,
  options,
  onChange,
  anyLabel,
  placeholder,
  isDisabled,
  className,
}: FilterSelectProps) {
  const selected = value === '' ? (anyLabel ? ANY : null) : value;
  return (
    <Select
      aria-label={label}
      value={selected}
      onChange={(next) => onChange(next === ANY || next === null ? '' : String(next))}
      placeholder={placeholder}
      isDisabled={isDisabled}
      className={cn('w-44', className)}
    >
      <SelectTrigger className="w-full border-border-secondary bg-input-background py-1.5">
        <SelectValue />
        <SelectIndicator />
      </SelectTrigger>
      <SelectContent className="min-w-(--trigger-width)">
        {anyLabel && (
          <SelectItem id={ANY} textValue={anyLabel}>
            {anyLabel}
          </SelectItem>
        )}
        {options
          .filter((option) => option.value !== '')
          .map((option) => (
            <SelectItem key={option.value} id={option.value} textValue={option.label}>
              {option.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

/** Each distinct value of a row field, with its count, as filter options sorted by value. */
export function facetOptions<T>(
  rows: readonly T[],
  pick: (row: T) => string | null | undefined,
  format: (value: string) => string = sentenceCase,
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = pick(row);
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({ value, label: `${format(value)} (${count})` }));
}

/** `market-data` → `Market data`, `read_only` → `Read only`. */
export function sentenceCase(value: string): string {
  const spaced = value.replace(/[-_.]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
